import { useEffect, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { X, Share } from 'lucide-react'
import { BRAND_SCOPE_ATTR } from '@/lib/brand-colors'
import { TENANT_APP_ICON_ATTR, TENANT_APP_NAME_ATTR } from '@/lib/tenant-app-identity'
import { useBrandShellNode } from '@/components/ui/brand-shell'
// Shared with lib/web-push.ts: the banner offers the install that Web Push
// on iOS requires, so both must read the same device off the same code.
import { isStandalone, isIOSSafari } from '@/lib/pwa-display'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISS_KEY = 'pwa-install-dismissed-at'
const INSTALLED_KEY = 'pwa-installed-at'

/**
 * The icon this browser would actually put on the home screen for EventRipple
 * itself — the same file `/manifest.webmanifest` names, and the same one
 * `netlify/edge-functions/manifest.ts` returns for every non-tenant surface.
 * The card used to draw a CSS «E» tile here, which is not the mark anybody
 * gets: the real one is the ripple on indigo. Showing a preview that differs
 * from the result is the same class of lie as the wrong tenant icon.
 * ⚠️ If the manifest's icon list changes, change this with it.
 */
const APP_ICON_SRC = '/pwa-192x192.png'

/**
 * Dismissed for the REST OF TODAY, not for a rolling window.
 *
 * David, 13.08.2026: «make the notification appear again from tomorrow, even if
 * people have Xed it out.» A rolling 24h (and the 7 DAYS this used to be) hides
 * it until the same clock time, so an X at 23:50 costs the whole of tomorrow
 * evening too. Comparing the calendar date is what «from tomorrow» actually
 * means, and it needs no timer.
 */
function isDismissed(): boolean {
  const raw = localStorage.getItem(DISMISS_KEY)
  if (!raw) return false
  const then = new Date(Number(raw))
  if (Number.isNaN(then.getTime())) return false
  const now = new Date()
  return (
    then.getFullYear() === now.getFullYear() &&
    then.getMonth() === now.getMonth() &&
    then.getDate() === now.getDate()
  )
}

/**
 * Already added it → never ask again. `appinstalled` has been WRITTEN here
 * since task 54 and never read, which was harmless while a dismissal lasted a
 * week and is not now: without this, someone who added the shortcut and then
 * opens the site in an ordinary tab gets asked again every single day.
 *
 * Only Chromium fires `appinstalled`, so this cannot cover iOS — there is no
 * such event and no API that answers «is it on the home screen». iOS is covered
 * the moment they open FROM the icon, because that is `display-mode: standalone`.
 */
function isInstalled(): boolean {
  return localStorage.getItem(INSTALLED_KEY) !== null
}

/**
 * Task 54, frame 08 følge 1. This banner installs an app that is PERMANENTLY
 * marked — on iOS and in the Dock the mark cannot be changed afterwards
 * without deleting and re-adding. So it must never be offered on a surface
 * carrying someone else's brand while the icon it would install is still ours.
 *
 * `data-brand-scope` is the existing signal, not a new one: `applyBrandVars`
 * sets it with the brand variables and removes it when they are cleared, and
 * `index.css` already resolves per-theme link and focus tokens off it.
 *
 * **Task 55 has since landed, so the rule is now the second half that note
 * promised: «brand present AND no icon of its own».** The second half is not a
 * second lookup of the same fact — `lib/tenant-app-identity.ts` sets
 * `data-tenant-app-icon` in the SAME code that rewrites `<link rel="manifest">`
 * to carry the tenant. So the banner can only appear where the manifest was
 * actually changed, and a surface can never advertise an install whose icon it
 * did not swap.
 *
 * In production today all three brand rows have a null `app_icon_url` (measured
 * 06.08.2026) — the icons are set by us, not by the customer — so every tenant
 * still takes the suppressed branch. That is the roll-out guard the task asks
 * for («en tenant som ennå ikke har fått ikon, skal ikke tilby et feil»), and
 * the banner starts appearing per tenant the moment an icon is set, with no
 * deploy.
 */
function isTenantBranded(): boolean {
  return document.querySelector(`[${BRAND_SCOPE_ATTR}]`) !== null
}

/**
 * The tenant mark this surface would install, or `null` for EventRipple's own.
 * Published by `applyTenantAppIdentity` in the same breath as the manifest
 * rewrite, so it can never disagree with what a tap actually installs.
 */
function readTenantMark(): { icon: string; name: string | null } | null {
  const root = document.documentElement
  const icon = root.getAttribute(TENANT_APP_ICON_ATTR)
  return icon ? { icon, name: root.getAttribute(TENANT_APP_NAME_ATTR) } : null
}

export function InstallPrompt() {
  const { t } = useTranslation()
  // Above every early return: `check-hook-order` forbids a hook after one,
  // and this one decides the card's PARENT, not whether it renders.
  const shell = useBrandShellNode()
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showBanner, setShowBanner] = useState(false)
  const [showIOSHint, setShowIOSHint] = useState(false)

  // The brand arrives from an async fetch, and on iOS Safari the banner shows
  // on mount — so a one-shot read would offer the wrong mark for the window
  // before the brand lands. Watch instead, and withdraw the offer if a brand
  // appears after the banner is already up.
  const [mark, setMark] = useState(readTenantMark)
  const [suppressed, setSuppressed] = useState(() => isTenantBranded() && !readTenantMark())
  /**
   * The icon file failed to load. Only then does the lettered tile appear —
   * it is a last resort, not the normal case. A tenant's `app_icon_url` is an
   * arbitrary upload and can 404; ours is a shipped asset and should not.
   */
  const [iconFailed, setIconFailed] = useState(false)
  useEffect(() => {
    const sync = () => {
      const m = readTenantMark()
      setMark(m)
      setSuppressed(isTenantBranded() && !m)
    }
    sync()
    const observer = new MutationObserver(sync)
    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      // Both halves of the rule, because they arrive from two different
      // effects: `applyBrandVars` sets the scope, `applyTenantAppIdentity` sets
      // the icon flag. Watching only the first would leave the banner
      // suppressed on a tenant that has since been given an icon.
      attributeFilter: [BRAND_SCOPE_ATTR, TENANT_APP_ICON_ATTR, TENANT_APP_NAME_ATTR],
    })
    return () => observer.disconnect()
  }, [])

  // Nobody knows whether anyone installs this at all, so the next decision here
  // gets made on data rather than on assertion (task 54, second half).
  useEffect(() => {
    const onInstalled = () => {
      try {
        localStorage.setItem(INSTALLED_KEY, String(Date.now()))
      } catch {
        /* private mode — the banner still works, we just learn nothing */
      }
      setShowBanner(false)
    }
    window.addEventListener('appinstalled', onInstalled)
    return () => window.removeEventListener('appinstalled', onInstalled)
  }, [])

  useEffect(() => {
    if (isStandalone() || isInstalled() || isDismissed()) return

    if (isIOSSafari()) {
      setShowIOSHint(true)
      setShowBanner(true)
      return
    }

    const handler = (e: Event) => {
      e.preventDefault()
      // Captured either way: `beforeinstallprompt` can only be caught once, and
      // without the event there is nothing to hand to `prompt()` later.
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      // Re-read rather than trusting the check at the top of this effect. That
      // one ran at MOUNT; this runs whenever the browser decides to fire, which
      // can be after the person has already pressed X in this same page. Found
      // by measuring, 13.08.2026: dismissing and then re-firing the event put
      // the card straight back. Chrome normally fires once per load, so it was
      // rare rather than impossible — and a dismissal that only sometimes holds
      // is worse than one that never did, because nobody can reproduce it.
      if (isDismissed() || isInstalled()) return
      setShowBanner(true)
    }

    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()))
    setShowBanner(false)
    setDeferredPrompt(null)
  }, [])

  const install = useCallback(async () => {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') setShowBanner(false)
    setDeferredPrompt(null)
  }, [deferredPrompt])

  // Task 54's ferdig-sentence: not on a tenant-branded surface without its own
  // icon; still on EventRipple's own surfaces.
  if (!showBanner || suppressed) return null

  const card = (
    <>
      <div className="pwa-banner" role="complementary" aria-label={t('pwa.bannerLabel')}>
        <div className="pwa-inner">
          <div className="pwa-gradient-bar" aria-hidden="true" />
          <div className="pwa-body">
            {/* A PREVIEW OF THE HOME SCREEN, not a decorative logo.
                Whatever is drawn here is what the phone will put on the home
                screen, from the same source the install reads: the tenant's
                mark where the manifest was rewritten to carry one, our own
                manifest icon everywhere else. It used to be a CSS «E» tile,
                which is not a mark anybody has ever received — the real one is
                the ripple on indigo (public/pwa-192x192.png). The lettered tile
                survives only for a dead image URL. */}
            {iconFailed ? (
              // Class only. The utilities that used to ride along here
              // (`text-[var(--color-neutral-0)]`, `text-caption`, the flex
              // trio) restated what `.pwa-logo` already declares, and one of
              // them contradicted it: they pinned the ink to white while the
              // rule now derives it from the fill. They never actually applied
              // — Tailwind's utilities are @layer'd and the `.pwa-*` rules in
              // index.css are not, so unlayered wins whatever the order — which is precisely
              // why they had to go rather than be trusted.
              <div className="pwa-logo" aria-hidden="true">E</div>
            ) : (
              <img
                className="pwa-logo pwa-logo-img"
                src={mark?.icon ?? APP_ICON_SRC}
                onError={() => setIconFailed(true)}
                alt=""
                aria-hidden="true"
                width={44}
                height={44}
              />
            )}

            {/* Text */}
            <div className="pwa-text">
              <p className="pwa-title">{mark?.name ?? 'EventRipple'}</p>
              {/* One sentence for both platforms. It used to branch to an
                  iOS-only string that said the same thing in different words,
                  while the steps below already carry the iOS «how». */}
              <p className="pwa-desc">{t('pwa.installApp')}</p>
            </div>

            {/* Actions */}
            <div className="pwa-actions">
              {!showIOSHint && (
                <button className="pwa-btn-install" onClick={install}>
                  {t('pwa.install')}
                </button>
              )}
              <button className="pwa-btn-close" onClick={dismiss} aria-label={t('common.close')}>
                <X size={15} />
              </button>
            </div>
          </div>

          {/* iOS instruction */}
          {showIOSHint && (
            <div className="pwa-ios-hint">
              {t('pwa.iosTap')}{' '}
              <Share
                size={13}
                className="inline align-icon"
                aria-hidden="true"
              />{' '}
              <strong>{t('pwa.iosShareButton')}</strong> →{' '}
              <strong>{t('pwa.iosAddToHome')}</strong>
            </div>
          )}
        </div>
      </div>
    </>
  )

  /**
   * Rendered INTO the brand-scoped shell whenever one is mounted, so every
   * token below resolves to the tenant's palette rather than EventRipple's.
   * `position: fixed` is unaffected: the shell sets no transform, filter or
   * containment, so the card still positions against the viewport, and its
   * `overflow: hidden` does not clip a fixed descendant.
   *
   * No shell (public and auth layouts) means the brand vars are on
   * `documentElement`, where ordinary inheritance already reaches this card,
   * so it stays exactly where it was.
   */
  return shell ? createPortal(card, shell) : card
}
